import { resolveActiveAuditOverrides, stripOverrideVersionSuffix } from './truthAttestations';
import { classifyDistanceMention } from './distanceRoles';
/**
 * R-67 — Within-report internal number consistency reconciler.
 */
import type { CanonicalFactKey } from '../../types/auditCanonicalFacts';
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import {
  buildEvidenceCacheFromSed,
  type EvidenceCache,
} from './providerPrecedenceVerifier';
import {
  normalizeToCanonical,
  parseCapacityMw,
  parseDistanceKm,
  parseAreaAcres,
  toInternalCanonicalKey,
  valuesWithinTolerance,
  type InternalCanonicalKey,
} from './unitNormalize';
import { AREA_MENTION_RE, classifyAcreageMention, type AreaCanonicalKey } from './acreageRoles';
import { extractSections, reportFullText } from './reportTextExtract';
import { LINE_LENGTH_DISTANCE_CONTEXT_RE, NON_FACILITY_MW_CONTEXT_RE, isComparisonBoundPrefix, isNonFacilityCapacityMention } from './capacityPolysemy';
// LOCKSTEP with the grounding gate (FIN-1, audit 3.8). `$0.142B` and `$142M`
// must denormalise to the SAME number in both engines: R-79 checks prose
// against the persisted financial stamp, this engine checks the prose against
// itself, and two different answers to "what does $142M mean?" produce
// contradictory guidance for one figure — the failure the shared
// `NON_FACILITY_MW_CONTEXT_RE` predicate exists to prevent for MW.
import {
  parseEnergyMwh,
  parsePercentPoints,
  parseUsdAmount,
} from '../grounding/groundingValidator';

/**
 * FIN-1 (audit 3.8) — the financial quantities this engine reconciles.
 *
 * A SEPARATE key per subject, never one `usd` or one `pct` bucket: a capacity
 * factor and an IRR are both percentages and are not the same quantity, so
 * bucketing them would report every financial report as self-contradictory.
 * The subject is anchored INTO each pattern (the `wetland_percent` precedent)
 * rather than inferred from the sentence, because a sentence routinely names
 * two of them at once ("the $500M CAPEX yields an NPV of $142M").
 */
export type FinancialCanonicalKey =
  | 'lcoe_usd_per_mwh'
  | 'npv_usd'
  | 'capex_total_usd'
  | 'capex_per_kw_usd'
  | 'irr_pct'
  | 'hurdle_rate_pct'
  | 'wacc_pct'
  | 'capacity_factor_pct'
  | 'payback_years'
  | 'annual_mwh';

/**
 * Every key this engine groups mentions under. Widened from
 * `InternalCanonicalKey` (which stays the SED/provider-backed namespace that
 * `unitNormalize` and `CanonicalFactKey` round-trip) so the financial keys can
 * exist without inventing public canonical-fact ids for quantities no provider
 * supplies.
 */
export type ReconcilerCanonicalKey = InternalCanonicalKey | FinancialCanonicalKey | AreaCanonicalKey;

const AREA_KEYS = new Set<ReconcilerCanonicalKey>(['survey_area_acres','wetland_area_acres','flood_area_acres','assessment_area_acres','net_area_acres','infrastructure_area_acres']);
function isAreaKey(key: ReconcilerCanonicalKey): key is AreaCanonicalKey { return AREA_KEYS.has(key); }

const FINANCIAL_KEYS: ReadonlySet<string> = new Set<FinancialCanonicalKey>([
  'lcoe_usd_per_mwh',
  'npv_usd',
  'capex_total_usd',
  'capex_per_kw_usd',
  'irr_pct',
  'hurdle_rate_pct',
  'wacc_pct',
  'capacity_factor_pct',
  'payback_years',
  'annual_mwh',
]);

function isFinancialKey(key: ReconcilerCanonicalKey): key is FinancialCanonicalKey {
  return FINANCIAL_KEYS.has(key);
}

/**
 * Normalize a parsed mention for comparison.
 *
 * Financial mentions arrive ALREADY normalized (each pattern's `parse` runs the
 * shared scale converters), so the money scale is resolved before any two
 * values are ever compared — `$0.142B` and `$142M` are both 142000000 here, and
 * the plain-USD `npvUsd` on the stamp needs no rescaling to meet them.
 */
function normalizeMentionValue(
  key: ReconcilerCanonicalKey,
  value: string | number,
  units: string | undefined,
): number | string {
  if (isAreaKey(key)) return normalizeToCanonical(value, units, 'acreage');
  if (isFinancialKey(key)) {
    if (typeof value === 'number') return value;
    const n = parseFloat(String(value).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : String(value).trim();
  }
  return normalizeToCanonical(value, units, key);
}

/**
 * Tolerance for two mentions of the same financial quantity.
 *
 * Rates and money get a RELATIVE band (prose rounds "$142,412,000" to "$142M");
 * percentages get an ABSOLUTE 0.1-point band, because a relative band on a 5%
 * IRR is ±0.05 pts while on a 92% capacity factor it is ±0.9 pts — the same
 * rule would be far too strict at one end and far too loose at the other.
 */
function withinTolerance(
  key: ReconcilerCanonicalKey,
  a: number | string,
  b: number | string,
): boolean {
  if (isAreaKey(key)) return valuesWithinTolerance('acreage', a, b);
  if (!isFinancialKey(key)) return valuesWithinTolerance(key, a, b);
  if (typeof a !== 'number' || typeof b !== 'number') {
    return String(a).trim().toUpperCase() === String(b).trim().toUpperCase();
  }
  switch (key) {
    case 'irr_pct':
    case 'hurdle_rate_pct':
    case 'wacc_pct':
    case 'capacity_factor_pct':
      return Math.abs(a - b) <= 0.1;
    case 'payback_years':
      return Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1) <= 0.02;
    default:
      return Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1) <= 0.01;
  }
}

export interface NumberMention {
  canonicalKey: ReconcilerCanonicalKey;
  areaScope?: string;
  areaLabel?: string;
  rawText: string;
  value: string | number;
  normalizedValue: number | string;
  units?: string;
  sectionId?: string;
  lineNumber: number;
  excerpt: string;
  /**
   * The FULL serialized line, for CLASSIFICATION only (scope +
   * co-occurrence). Never persisted and never shown.
   *
   * `excerpt` is capped at 160 chars because an operator reads it, and both
   * classifiers were reading that cap as if it were the sentence. On the live
   * supply-chain body one flattened risk row says, in a single breath,
   * "…the financial model's Phase 1 CAPEX of $80M and the indicative price of
   * $240M for the full BTM generator package" — the report EXPLAINING a
   * discrepancy, not contradicting itself — and both figures sat past
   * character 160, so the co-occurrence rule could not see either one.
   *
   * This is also required for scoped physical quantities. A structured
   * finding such as "Project Schedule Conflicts … | Detail: … initial 50 MW
   * service" can put the phase qualifier beyond character 160. Classifying
   * only the display excerpt then buckets that legitimate first-phase value
   * with the 550 MW ultimate campus total and manufactures a contradiction.
   * The persistence boundary below deliberately strips `scopeText`; it is an
   * audit working value, not reader-facing evidence.
   */
  scopeText?: string;
  /** Internal full sentence identity for unit-aware source-conflict disclosure. */
  sentenceScope?: string;
  /** Local explicit distance asset name; never changes the existing vote scopes. */
  distanceAssetScope?: string;
  /** Numeric-token-bound asset identity, when voltage grammar identifies it. */
  quantityScope?: string;
  /** True when line cites a tier-1 provider source. */
  tier1Cited: boolean;
  provenance: 'prose' | 'table';
}

export interface InternalNumberFinding {
  canonicalKey: ReconcilerCanonicalKey;
  label: string;
  severity: 'block' | 'warn';
  mentions: NumberMention[];
  conflictingValues: Array<number | string>;
  resolution?: CanonicalResolution;
  /** When true, autocorrect is blocked (tier-1 cited value disagrees with canonical). */
  autocorrectBlocked?: boolean;
}

export interface CanonicalResolution {
  canonicalValue: number | string;
  source: 'tier1' | 'majority' | 'provenance';
  tier1Source?: string;
  provenance?: string;
}

export type InternalNumberFixMode = 'flagOnly' | 'autocorrect';

const FACT_LABELS: Record<ReconcilerCanonicalKey, string> = {
  capacity_mw: 'Capacity (MW)',
  interconnection_distance_km: 'Interconnection / substation distance (km)',
  nuclear_proximity_km: 'Nearest nuclear reactor distance (km)',
  flood_zone: 'FEMA flood zone',
  voltage_kv: 'Substation voltage (kV)',
  acreage: 'Site acreage',
  survey_area_acres: 'Survey footprint',
  wetland_area_acres: 'Wetland acreage',
  flood_area_acres: 'Flood acreage',
  assessment_area_acres: 'Assessment footprint',
  net_area_acres: 'Net or usable acreage',
  infrastructure_area_acres: 'Infrastructure footprint',
  wetland_percent: 'Wetland coverage (%)',
  nearest_substation: 'Nearest substation',
  grid_operator: 'Grid operator / ISO',
  water_source_type: 'Water source type',
  census_tract: 'Census tract',
  // FIN-1 (audit 3.8)
  lcoe_usd_per_mwh: 'LCOE ($/MWh)',
  npv_usd: 'Project NPV ($)',
  capex_total_usd: 'Total CAPEX ($)',
  capex_per_kw_usd: 'CAPEX per kW ($/kW)',
  irr_pct: 'Project IRR (%)',
  hurdle_rate_pct: 'IRR hurdle rate (%)',
  wacc_pct: 'WACC / discount rate (%)',
  capacity_factor_pct: 'Capacity factor (%)',
  payback_years: 'Payback period (years)',
  annual_mwh: 'Annual generation (MWh)',
};

interface PatternDef {
  key: ReconcilerCanonicalKey;
  re: RegExp;
  contextRe?: RegExp;
  /**
   * L-split mirror (2026-08-13): a sentence matching this is about a
   * DIFFERENT quantity sharing the unit (an IX/peering or fiber distance on
   * a grid-flavoured line) — skipped entirely, mirroring the audit
   * extractors' polysemy guards.
   */
  negativeContextRe?: RegExp;
  parse: (m: RegExpMatchArray, line: string) => { value: string | number; units?: string; rawText: string } | null;
}

const TIER1_CITATION_RE = /\[PROVIDER:\s*[A-Z0-9_-]+\]|(?:per|from|via)\s+"[^"]*(?:HIFLD|FEMA|USFWS|NRC|NREL|USGS|EIA|PEERINGDB|WRI|AIRNOW)[^"]*"/i;

/* ── FIN-1 (audit 3.8) — the financial quantity patterns ─────────────────
 *
 * SUBJECT-ANCHORED, both directions ("NPV of $142M" and "$142M NPV"), with a
 * bounded same-sentence gap. This follows the `wetland_percent` pattern above
 * rather than the `contextRe` route the capacity/distance keys use, and the
 * difference is load-bearing: `contextRe` is tested against the whole
 * SENTENCE, and one financial sentence routinely names several of these
 * quantities at once —
 *
 *   "The $500M CAPEX yields an NPV of $142M at a 9.2% WACC."
 *
 * `line.match()` returns the FIRST hit, so a sentence-scoped NPV rule would
 * have logged $500M as the NPV. Anchoring the subject INTO the pattern keeps
 * each figure with the quantity it was written for.
 *
 * A figure whose subject is not named is left UNKEYED — silence, not a guess.
 * That is the same fail-open direction the MW polysemy guards take: a missed
 * catch is recoverable, a manufactured contradiction holds a correct report.
 */
/** `$142M` / `$0.142B` / `$-42.0M` / `$1,850` — group 1 = figure, 2 = scale. */
const MONEY_SRC = String.raw`\$\s?(-?[\d,]+(?:\.\d+)?)\s?(trillion|billion|million|thousand|tn|bn|mm|[tbmk])?\b`;
/** A rate cannot vote as a whole-project total. Reject its complete prefix
 * before matching money, so regex backtracking cannot shorten $1,850/kW to
 * $1 or drop "thousand" from $1.85 thousand/kW. Other totals on the same
 * line remain eligible; never suppress the sentence containing the rate. */
const CAPEX_RATE_UNIT_SRC = String.raw`(?:[kmg]?W(?:h)?|SF|square\s+(?:feet|foot|meters?|metres?)|sq\.?\s*(?:ft|m)|(?:ft|m)[2²]|units?|sets?|modules?|cabinets?|years?|yrs?|months?|days?|hours?|hrs?)(?![A-Za-z0-9])`;
// "Per the owner estimate" attributes a dollar total to its source, rather
// than supplying a physical denominator, and must remain a total assertion.
const TOTAL_CAPEX_MONEY_SRC = String.raw`(?!\$\s*-?[\d,]+(?:\.\d+)?\s*(?:trillion|billion|million|thousand|tn|bn|mm|[tbmk])?\s*(?:\/\s*|per\s+)(?:(?:the|installed|rated|facility|IT|billed)\s+){0,3}${CAPEX_RATE_UNIT_SRC})${MONEY_SRC}`;
/** `$78/MWh`, `$78 per MWh`. */
const RATE_MWH_SRC = String.raw`\$\s?([\d,]+(?:\.\d+)?)\s?(?:\/\s?|\s?per\s+)MWh\b`;
/** `$1,850/kW` — `(?!h)` keeps `$/kWh` out; the yr guard keeps fixed O&M out. */
const RATE_KW_SRC = String.raw`\$\s?([\d,]+(?:\.\d+)?)\s?(?:\/\s?|\s?per\s+)kW\b(?!h)(?!\s?-?\s?(?:yr|year))`;
const PCT_SRC = String.raw`([\d,]+(?:\.\d+)?)\s?%`;
const YEARS_SRC = String.raw`([\d,]+(?:\.\d+)?)\s?(?:yrs?|years?)\b`;
const ENERGY_SRC = String.raw`([\d,]+(?:\.\d+)?)\s?(MWh|GWh|TWh)\b`;

/** Terms of art ONLY — a topic word ("cost", "finance") discriminates nothing. */
const SUBJECT_SRC = {
  npv: String.raw`\b(?:NPV|net present value)\b`,
  capex: String.raw`\b(?:CAPEX|capital expenditure|capital cost|installed cost|overnight cost)\b`,
  lcoe: String.raw`\b(?:LCOE|level(?:i[sz]ed|ised|ized)\s+cost(?:\s+of\s+(?:energy|electricity))?)\b`,
  irr: String.raw`\b(?:IRR|internal rate of return)\b`,
  hurdle: String.raw`\b(?:hurdle|target return|required return)\b`,
  wacc: String.raw`\b(?:WACC|weighted[- ]average cost of capital|discount rate|cost of capital)\b`,
  capacityFactor: String.raw`\bcapacity factor\b`,
  payback: String.raw`\bpayback\b`,
  annualEnergy: String.raw`\b(?:annual (?:generation|output|energy|production)|generation per year|energy (?:production|output) per year)\b`,
} as const;

/**
 * The gap between a subject and its figure.
 *
 * TEMPERED — the gap may not cross ANOTHER subject term. A plain bounded gap
 * bound the wrong figure the moment a sentence named two quantities:
 *
 *   "The $500M CAPEX yields an NPV of $142M."
 *
 * `CAPEX .{0,28}? MONEY` reaches past "NPV" to $142M and logs the NPV as the
 * CAPEX; `MONEY .{0,20}? NPV` reaches back past "CAPEX" and logs the CAPEX as
 * the NPV. Refusing to step over an intervening subject makes each figure bind
 * to the quantity it was actually written for, without having to shrink the gap
 * to something real prose never fits in.
 *
 * The value→subject direction is separately TIGHT (10). In English that form is
 * essentially adjacent — "13.4% IRR", "$142M NPV", "9.0% hurdle", "13.4%
 * project IRR" — while a wide reverse gap let "…5.2% remains below the required
 * return…" bind the IRR to the hurdle.
 */
const ANY_SUBJECT_SRC = Object.values(SUBJECT_SRC).join('|');
const gapSrc = (maxChars: number): string =>
  String.raw`(?:(?!(?:${ANY_SUBJECT_SRC}))[^.\n]){0,${maxChars}}?`;
const SUBJECT_GAP = gapSrc(28);
const VALUE_GAP = gapSrc(10);

function financialPatterns(): PatternDef[] {
  const out: PatternDef[] = [];
  const add = (
    key: ReconcilerCanonicalKey,
    subject: string,
    valueSrc: string,
    units: string,
    parseValue: (m: RegExpMatchArray, offset: number) => number | null,
  ): void => {
    // subject → value: the capture groups sit AFTER the subject (offset 1).
    out.push({
      key,
      re: new RegExp(`${subject}${SUBJECT_GAP}${valueSrc}`, 'i'),
      parse: (m) => {
        const v = parseValue(m, 1);
        return v != null ? { value: v, units, rawText: m[0] } : null;
      },
    });
    // value → subject: the subject sub-patterns are all NON-CAPTURING, so the
    // figure's groups still start at 1 in both directions.
    out.push({
      key,
      re: new RegExp(`${valueSrc}${VALUE_GAP}${subject}`, 'i'),
      parse: (m) => {
        const v = parseValue(m, 1);
        return v != null ? { value: v, units, rawText: m[0] } : null;
      },
    });
  };

  const money = (m: RegExpMatchArray, i: number) => parseUsdAmount(m[i]!, m[i + 1]);
  const plainMoney = (m: RegExpMatchArray, i: number) => parseUsdAmount(m[i]!);
  const pct = (m: RegExpMatchArray, i: number) => parsePercentPoints(m[i]!);
  const years = (m: RegExpMatchArray, i: number) => {
    const n = parseFloat(m[i]!.replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  };
  const energy = (m: RegExpMatchArray, i: number) => parseEnergyMwh(m[i]!, m[i + 1]);

  add('npv_usd', SUBJECT_SRC.npv, MONEY_SRC, 'USD', money);
  add('capex_total_usd', SUBJECT_SRC.capex, TOTAL_CAPEX_MONEY_SRC, 'USD', money);
  add('lcoe_usd_per_mwh', SUBJECT_SRC.lcoe, RATE_MWH_SRC, 'USD/MWh', plainMoney);
  add('capex_per_kw_usd', SUBJECT_SRC.capex, RATE_KW_SRC, 'USD/kW', plainMoney);
  add('irr_pct', SUBJECT_SRC.irr, PCT_SRC, '%', pct);
  add('hurdle_rate_pct', SUBJECT_SRC.hurdle, PCT_SRC, '%', pct);
  add('wacc_pct', SUBJECT_SRC.wacc, PCT_SRC, '%', pct);
  add('capacity_factor_pct', SUBJECT_SRC.capacityFactor, PCT_SRC, '%', pct);
  add('payback_years', SUBJECT_SRC.payback, YEARS_SRC, 'years', years);
  add('annual_mwh', SUBJECT_SRC.annualEnergy, ENERGY_SRC, 'MWh', energy);
  return out;
}

const NUMBER_PATTERNS: PatternDef[] = [
  /*
   * LOCKSTEP with R-79 (2026-08-14, Triple Threat RCA). Both capacity
   * patterns carry the SHARED non-facility-MW guard. Without it this engine
   * bucketed the project's 10 MW and the utility line's 26 MW headroom under
   * one `capacity_mw` key across 50 mentions, majority-voted to 10, and
   * offered "Settled → 10" — a fix that rewrites grid headroom. Meanwhile the
   * grounding gate blocked the same sentences demanding 26. The second
   * pattern below has NO contextRe at all (it matches every bare "N MW" in the
   * document), so omitting the guard there would have left the hole wide open.
   */
  {
    key: 'capacity_mw',
    re: /\b([\d,]+(?:\.\d+)?)\s*[-\s]?(MW|GW|megawatts?)\b/i,
    contextRe: /(\bMW\b|capacity|power requirement|total power|campus|facility|nameplate|demand)/i,
    negativeContextRe: NON_FACILITY_MW_CONTEXT_RE,
    parse: (m) => {
      const v = parseCapacityMw(m[1], m[2]);
      return v != null ? { value: v, units: 'MW', rawText: m[0] } : null;
    },
  },
  {
    key: 'capacity_mw',
    re: /\b([\d,]+(?:\.\d+)?)\s*[-\s]?MW\b/i,
    negativeContextRe: NON_FACILITY_MW_CONTEXT_RE,
    parse: (m) => {
      const v = parseCapacityMw(m[1], 'MW');
      return v != null ? { value: v, units: 'MW', rawText: m[0] } : null;
    },
  },
  {
    key: 'interconnection_distance_km',
    re: /\b([\d,]+(?:\.\d+)?)\s*[-\s]?(km|mi|miles?|kilometers?)\b/i,
    // L-split (2026-08-13, Redhead): IX\b|peering were IN this context list,
    // so the 204.9 km IX distance bucketed as an "interconnection/substation
    // distance" and false-blocked against the genuine 2.9 km substation
    // figure within one report. IX/peering/fiber are DIFFERENT distances —
    // same namespace split the audit extractors already carry.
    contextRe: /(interconnect|substation|transmission|grid access|nearest sub)/i,
    // + line-length guard (2026-08-18, Redhead `stne8w`): "3.4 km
    // transmission line to a 230 kV corridor" is a build quantity, not the
    // substation distance. SHARED with the cross-report reconciler.
    negativeContextRe: LINE_LENGTH_DISTANCE_CONTEXT_RE,
    parse: (m) => {
      const v = parseDistanceKm(m[1], m[2]);
      return v != null ? { value: v, units: 'km', rawText: m[0] } : null;
    },
  },
  {
    key: 'nuclear_proximity_km',
    re: /\b([\d,]+(?:\.\d+)?)\s*[-\s]?(km|mi|miles?|kilometers?)\b/i,
    contextRe: /(nuclear|reactor|NRC)/i,
    parse: (m) => {
      const v = parseDistanceKm(m[1], m[2]);
      return v != null ? { value: v, units: 'km', rawText: m[0] } : null;
    },
  },
  {
    key: 'flood_zone',
    re: /\bzone\s+(A(?:E|H|O)?|AE|AO|AH|VE|V|X|D|B|C)\b/i,
    contextRe: /(flood|FEMA|SFHA)/i,
    parse: (m) => ({ value: m[1].toUpperCase(), rawText: m[0] }),
  },
  {
    key: 'flood_zone',
    re: /\bFEMA\s+(?:100[- ]yr\s+)?(?:flood\s+)?zone\s+(A(?:E|H|O)?|AE|AO|AH|VE|V|X|D|B|C)\b/i,
    parse: (m) => ({ value: m[1].toUpperCase(), rawText: m[0] }),
  },
  {
    key: 'voltage_kv',
    re: /\b([\d,]+(?:\.\d+)?)\s*[-\s]?kV\b/i,
    contextRe: /(substation|switchyard|\bbus\b|transmission|voltage|grid)/i,
    parse: (m) => ({
      value: parseFloat(m[1].replace(/,/g, '')),
      units: 'kV',
      rawText: m[0],
    }),
  },
  {
    key: 'acreage',
    re: /\b([\d,]+(?:\.\d+)?)\s*[-\s]?(acres?|ac\.?|ha|hectares?)\b/i,
    contextRe: /(site (area|acreage)|parcel|footprint|acres)/i,
    parse: (m) => {
      const v = parseAreaAcres(m[1], m[2]);
      return v != null ? { value: v, units: 'acres', rawText: m[0] } : null;
    },
  },
  {
    key: 'wetland_percent',
    re: /\b([\d,]+(?:\.\d+)?)\s*%\s*(?:of\s+(?:the\s+)?(?:site|parcel))?\s*wetland/i,
    parse: (m) => ({ value: parseFloat(m[1]), units: '%', rawText: m[0] }),
  },
  {
    key: 'wetland_percent',
    re: /wetland[^.\n]{0,80}\b([\d,]+(?:\.\d+)?)\s*%/i,
    parse: (m) => ({ value: parseFloat(m[1]), units: '%', rawText: m[0] }),
  },

  ...financialPatterns(),
];

const PROVENANCE_RANK: Record<NumberMention['provenance'], number> = {
  table: 2,
  prose: 1,
};

export function canonicalFactKeyFromInternal(key: ReconcilerCanonicalKey): CanonicalFactKey | null {
  const map: Partial<Record<ReconcilerCanonicalKey, CanonicalFactKey>> = {
    capacity_mw: 'capacityMW',
    interconnection_distance_km: 'interconnectionDistanceKm',
    nuclear_proximity_km: 'nuclearProximityKm',
    flood_zone: 'floodZone',
    voltage_kv: 'voltageKV',
    acreage: 'acreage',
    wetland_percent: 'wetlandPercent',
    nearest_substation: 'nearestSubstation',
    grid_operator: 'gridOperator',
    water_source_type: 'waterSourceType',
    census_tract: 'censusTract',
  };
  return map[key] ?? null;
}

function isTier1CitedLine(line: string): boolean {
  return TIER1_CITATION_RE.test(line);
}

/**
 * The sentence containing `index`, used to scope a number's qualifying context.
 *
 * A figure is qualified by the SENTENCE it sits in, not by its whole paragraph.
 * Testing `contextRe` against the entire line let one sentence qualify a number
 * in another: on a real test-fit body, "Fiber optic connectivity will require a
 * new lateral build of approximately 5 miles" was scored as an
 * `interconnection_distance_km` mention because an EARLIER sentence in the same
 * paragraph said "A new 10-acre substation will be constructed on-site". That
 * 8.05 km fiber run then "contradicted" the real 3.01 km substation distance and
 * BLOCKED the report — a fiber lateral and a substation distance are simply not
 * the same measurement. Same family as the false `ix.distanceKm` blocks.
 */
function sentenceAround(line: string, index: number): string {
  const before = Math.max(
    line.lastIndexOf('. ', index),
    line.lastIndexOf('? ', index),
    line.lastIndexOf('! ', index),
  );
  const from = before === -1 ? 0 : before + 1;
  const ends = ['. ', '? ', '! ']
    .map((terminator) => line.indexOf(terminator, index))
    .filter((at) => at !== -1);
  const to = ends.length > 0 ? Math.min(...ends) + 1 : line.length;
  return line.slice(from, to);
}

/** Bind an explicit named substation/reactor to its nearby distance token.
 * Generic "the substation" and provider names are not new asset identities. */
function distanceAssetScope(sentence: string, index: number): string | undefined {
  const names = [
    ...[...sentence.matchAll(/\b([A-Z][\w'’-]*(?:\s+[A-Z][\w'’-]*){0,3})\s+(?:[Ss]ubstation|[Ss]witchyard|[Rr]eactor|[Nn]uclear\s+[Pp]lant)\b/g)]
      .filter(match => !/^(?:The|This|That|A|An|Nearest|Existing|Proposed|Nearby)$/i.test(match[1]!)),
    ...[...sentence.matchAll(/\b([A-Z]{2,}[0-9]{3,}[A-Z0-9_-]*)\b/g)]
      .filter(match => !/^(?:FERC|IEEE|NERC|ASCE|NFPA|USGS|FEMA|EPA|USDA|NREL)$/i.test(match[1]!)),
  ];
  names.sort((a, b) => Math.abs(a.index! - index) - Math.abs(b.index! - index));
  return names[0]?.[1]?.toLowerCase().replace(/\s+/g, ' ');
}

/** Identify the asset that owns this voltage, not another asset elsewhere
 * in the paragraph (e.g. a substation followed by other nearby line voltages). */
function voltageAssetScope(sentence: string, raw: string): string | undefined {
  const at = sentence.indexOf(raw);
  if (at < 0) return undefined;
  const after = sentence.slice(at + raw.length);
  if (/^\s*(?:(?:,|and|or|\/)\s*\d+(?:\.\d+)?\s*kV\s*){0,3}(?:(?:transmission|grid|regional|network|interconnection|bulk)\s+)?(?:lines?|circuits?|corridors?|feeds?|diversity|interties?|interfaces?|backbone)\b/i.test(after)) return 'transmission-line';
  if (/^\s*(?:distribution\s+)?feeders?\b/i.test(after)) return 'distribution';
  const before = sentence.slice(0, at);
  if (/\bdual[- ]substation\s*$/i.test(before)) return 'transmission-line';
  const assetAfter = /^\s*(?:(?!dual[- ]substation\b)[A-Z0-9][\w'-]*\s+){0,2}(?:substation|switchyard|bus)\b/i.test(after);
  // Proposed onsite equipment and the existing utility substation are not
  // the same asset. The cue must bind directly to this voltage/asset.
  if (assetAfter && /\b(?:proposed|planned|new)\s+(?:(?:on[- ]site|onsite|project|customer|dedicated)\s+){0,2}$/i.test(before)) return 'proposed-substation';
  if (assetAfter) return 'substation';
  if (/(?<!dual[- ])\b(?:substation|switchyard)\b[^,;]{0,140}$/i.test(before)) return 'substation';
  if (/\b(?:transmission\s+)?(?:line|circuit|corridor)\b[^,;]{0,100}$/i.test(before)) return 'transmission-line';
  return undefined;
}

function scanLine(
  line: string,
  lineNumber: number,
  sectionId: string | undefined,
  source: NumberMention['provenance'],
  sectionFilter?: string,
): NumberMention[] {
  if (sectionFilter && sectionId !== sectionFilter) return [];
  const out: NumberMention[] = [];
  for (const pat of NUMBER_PATTERNS) {
    const distanceRole = pat.key === 'interconnection_distance_km' ? 'substation' : pat.key === 'nuclear_proximity_km' ? 'reactor' : null;
    const matches = pat.key === 'acreage'
      ? [...line.matchAll(new RegExp(AREA_MENTION_RE.source, 'gi'))]
      : distanceRole || pat.key === 'capacity_mw' ? [...line.matchAll(new RegExp(pat.re.source, 'gi'))]
      : [line.match(pat.re)].filter((match): match is RegExpMatchArray => match !== null);
    for (const m of matches) {
      if (pat.key === 'capacity_mw' && isNonFacilityCapacityMention(line, m.index, m[0].length)) continue;
      if (distanceRole && classifyDistanceMention(line, m.index ?? 0, m[0].length) !== distanceRole) continue;
      const scope = m.index === undefined ? line : sentenceAround(line, m.index);
      if (pat.key !== 'acreage' && pat.contextRe && !pat.contextRe.test(scope)) continue;
      /*
       * ASYMMETRIC WINDOWS (2026-08-14). The positive context must be TIGHT (the
       * sentence) — a keyword three sentences away does not make this figure a
       * capacity. The negative context must be WIDE (the whole line, which is
       * the paragraph a markdown report emits, and is exactly the `excerpt` the
       * operator is shown) because the disqualifying clause routinely sits in
       * the PRECEDING sentence:
       *
       *   "Available grid capacity is unconfirmed. The user-provided estimate
       *    of 26 MW on the 34.5kV line ... must be treated as preliminary."
       *
       * Sentence-scoped, the guard saw only the second sentence and admitted
       * 26 MW as a facility capacity. Widening can only ever REMOVE a mention
       * from a vote, never flip one — a strictly safer direction than the
       * data-corrupting settle it prevents.
       */
      if (!distanceRole && pat.negativeContextRe && pat.negativeContextRe.test(line)) continue;
      // LOCKSTEP with R-79 (Pronghorn 2026-08-17): a comparison-BOUND prefix
      // ("(<50 MW)", "under 10 MW") states a bound, not a point assertion, and
      // must not enter the vote. Syntax-level, subject-agnostic (shared guard).
      if (pat.key === 'capacity_mw' && isComparisonBoundPrefix(line, m.index)) continue;
      const parsed = pat.parse(m, line);
      if (!parsed) continue;
      const normalizedValue = normalizeMentionValue(pat.key, parsed.value, parsed.units);
      const area = pat.key === 'acreage' ? classifyAcreageMention(line, m.index ?? 0, m[0].length) : null;
      if (pat.key === 'acreage' && !area) continue;
      out.push({
        canonicalKey: area?.key ?? pat.key,
        ...(area ? { areaScope: area.scope, areaLabel: area.label } : {}),
        rawText: parsed.rawText,
        value: parsed.value,
        normalizedValue,
        units: parsed.units,
        sectionId,
        lineNumber,
        excerpt: line.trim().slice(0, 160),
        scopeText: line.trim().slice(0, SCOPE_TEXT_MAX),
        sentenceScope: scope,
        ...(distanceRole ? { distanceAssetScope: distanceAssetScope(scope, scope.indexOf(m[0])) } : {}),
        ...(pat.key === 'voltage_kv' ? { quantityScope: voltageAssetScope(scope, parsed.rawText) } : {}),
        tier1Cited: isTier1CitedLine(line),
        provenance: source,
      });
    }
  }
  return out;
}

/** Extract all numeric mentions from markdown, optionally scoped to one section. */
export function extractNumberMentions(markdown: string, sectionId?: string): NumberMention[] {
  const mentions: NumberMention[] = [];
  const lines = markdown.split('\n');
  let currentSection: string | undefined;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const heading = line.match(/^#{1,4}\s+(\S[\s\S]*?)\s*$/);
    if (heading) currentSection = heading[1]!.trim();
    mentions.push(...scanLine(line, i + 1, currentSection, 'prose', sectionId));

    if (line.includes('|')) {
      const cells = line.split('|').map((c) => c.trim()).filter(Boolean);
      if (cells.length >= 2) {
        const label = cells[0]!.toLowerCase();
        const valCell = cells.slice(1).join(' ');
        // Same non-facility-MW guard the prose patterns carry — a
        // "Total Capacity | Unverified 26 MW on adjacent 34.5kV line" row
        // states HEADROOM, not the facility's capacity.
        if (/capacity|power/.test(label) && !NON_FACILITY_MW_CONTEXT_RE.test(line)) {
          const m = valCell.match(/\b([\d,]+(?:\.\d+)?)\s*[-\s]?(MW|GW|megawatts?)\b/i);
          // Pronghorn — "| Capacity | <50 MW |" states a bound, not a value.
          if (m && !isComparisonBoundPrefix(valCell, m.index)
            && !isNonFacilityCapacityMention(line, line.indexOf(m[0]), m[0].length)) {
            const v = parseCapacityMw(m[1], m[2]);
            if (v != null) {
              mentions.push({
                canonicalKey: 'capacity_mw',
                rawText: m[0],
                value: v,
                normalizedValue: v,
                units: 'MW',
                sectionId: currentSection,
                lineNumber: i + 1,
                excerpt: line.trim().slice(0, 160),
                tier1Cited: isTier1CitedLine(line),
                provenance: 'table',
              });
            }
          }
        }
        if (/substation|interconnect/.test(label) && !/radius|search|buffer/.test(label)
          && !/^-?\s*(?:origin|confidence|section\s*id|sources?|id|claim\s*id|category|kind|status|severity|impact|action|description|finding|risk)\s*:/i.test(cells[0]!)) {
          const m = valCell.match(/\b([\d,]+(?:\.\d+)?)\s*[-\s]?(km|mi|miles?|kilometers?)\b/i);
          if (m) {
            const v = parseDistanceKm(m[1], m[2]);
            if (v != null) {
              mentions.push({
                canonicalKey: 'interconnection_distance_km',
                rawText: m[0],
                value: v,
                normalizedValue: v,
                units: 'km',
                sectionId: currentSection,
                lineNumber: i + 1,
                excerpt: line.trim().slice(0, 160),
                tier1Cited: isTier1CitedLine(line),
                provenance: 'table',
              });
            }
          }
        }
      }
    }
  }
  return mentions;
}

function roundsConvertedMentionToCanonical(
  mention: NumberMention,
  canonical: number | string,
): boolean {
  if (typeof canonical !== 'number' || typeof mention.normalizedValue !== 'number') return false;
  const rawMatch = mention.rawText.match(/([\d,]+(?:\.(\d+))?)/);
  if (!rawMatch) return false;
  const statedValue = parseFloat(rawMatch[1]!.replace(/,/g, ''));
  if (!Number.isFinite(statedValue) || statedValue === 0) return false;
  if (Math.abs(mention.normalizedValue - statedValue) <= 1e-9) return false;
  const canonicalPerStatedUnit = mention.normalizedValue / statedValue;
  if (!Number.isFinite(canonicalPerStatedUnit) || canonicalPerStatedUnit === 0) return false;
  const decimals = rawMatch[2]?.length ?? 0;
  return Number((canonical / canonicalPerStatedUnit).toFixed(decimals)) === statedValue;
}

function distinctNormalizedValues(
  key: ReconcilerCanonicalKey,
  mentions: NumberMention[],
): Array<number | string> {
  const distinctMentions: NumberMention[] = [];
  for (const m of mentions) {
    const matched = distinctMentions.some(
      (d) =>
        withinTolerance(key, d.normalizedValue, m.normalizedValue) ||
        roundsConvertedMentionToCanonical(m, d.normalizedValue) ||
        roundsConvertedMentionToCanonical(d, m.normalizedValue),
    );
    if (!matched) distinctMentions.push(m);
  }
  return distinctMentions.map((m) => m.normalizedValue);
}

/** How much of a serialized line is kept for classification. */
const SCOPE_TEXT_MAX = 1200;

/**
 * Every money / percent / duration / energy figure in `text`, NORMALISED.
 *
 * The literal-substring test below cannot see a scaled figure: a normalised
 * 142000000 is written "$142M" in prose, so `excerptStatesValue` reported
 * "this line does not state that value" about a line that states it plainly.
 * Scale has to be resolved on BOTH sides before a co-occurrence — or a
 * contradiction — can be decided.
 */
function financialFiguresIn(text: string): number[] {
  const out: number[] = [];
  const push = (v: number | null) => { if (v != null && Number.isFinite(v)) out.push(v); };

  const money = /(?:\$|\bUSD\s)\s?(-?[\d,]+(?:\.\d+)?)(?![\d.,])(?:\s?(trillion|billion|million|thousand|tn|bn|mm|[tbmk])\b)?/gi;
  const percent = /([\d,]+(?:\.\d+)?)\s?%/g;
  const durations = /([\d,]+(?:\.\d+)?)\s?(?:yrs?|years?)\b/gi;
  const energy = /([\d,]+(?:\.\d+)?)\s?(MWh|GWh|TWh)\b/gi;

  let m: RegExpExecArray | null;
  while ((m = money.exec(text)) !== null) push(parseUsdAmount(m[1]!, m[2]));
  while ((m = percent.exec(text)) !== null) push(parsePercentPoints(m[1]!));
  while ((m = durations.exec(text)) !== null) {
    const n = parseFloat(m[1]!.replace(/,/g, ''));
    push(Number.isFinite(n) ? n : null);
  }
  while ((m = energy.exec(text)) !== null) push(parseEnergyMwh(m[1]!, m[2]));
  return out;
}

/**
 * True when `excerpt` states `value` — literally for the SED/provider keys,
 * SCALE-NORMALISED for the financial ones.
 *
 * Word-boundary anchored so "50" does not match inside "550" or "1,500", and
 * thousands separators are tolerated because report prose writes "1,170.1".
 */
function excerptStatesValue(
  key: ReconcilerCanonicalKey,
  excerpt: string,
  value: number | string,
): boolean {
  // A FEMA zone is a label, not a number: "intersects Zone A (31 acres) and
  // Zone AE (109 acres)" and "Zone A/AE" state two zones in one excerpt. The
  // zone word is required and the zone letters are matched upper-case, so
  // "a 5 A breaker" or "the zone a portion of" never counts.
  if (key === 'flood_zone' && typeof value === 'string') {
    const zone = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!zone) return false;
    return new RegExp(`\\b(?:[Zz]ones?|ZONES?)\\s+(?:[A-Z][A-Z0-9]{0,3}\\s*(?:\\/|,|&|and|or)\\s*)*${zone}\\b`).test(excerpt);
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  if (isFinancialKey(key)) {
    return financialFiguresIn(excerpt).some((figure) => withinTolerance(key, figure, value));
  }
  const plain = String(value);
  const grouped = value.toLocaleString('en-US');
  const alternatives = plain === grouped ? [plain] : [plain, grouped];
  return alternatives.some((literal) => {
    const escaped = literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?<![\\d.,])${escaped}(?![\\d.,])`).test(excerpt);
  });
}

/** Resolve the canonical value for a set of mentions. Priority: tier-1 → majority → provenance. */
export function resolveCanonicalValue(
  mentions: NumberMention[],
  _report: SiteRagReport,
  evidence: EvidenceCache,
): CanonicalResolution | null {
  if (mentions.length === 0) return null;
  const key = mentions[0]!.canonicalKey;
  if (isAreaKey(key)) return null;
  const factKey = canonicalFactKeyFromInternal(key);

  if (factKey && evidence.providerData[factKey]) {
    const tier1 = evidence.providerData[factKey]!;
    return {
      canonicalValue: tier1.value,
      source: 'tier1',
      tier1Source: tier1.tier1Source,
      provenance: tier1.provenance,
    };
  }

  const buckets = new Map<string, { count: number; value: number | string; provenance: number }>();
  for (const m of mentions) {
    const bucketKey = String(m.normalizedValue);
    const existing = buckets.get(bucketKey);
    const provRank = PROVENANCE_RANK[m.provenance];
    if (existing) {
      existing.count += 1;
      existing.provenance = Math.max(existing.provenance, provRank);
    } else {
      buckets.set(bucketKey, { count: 1, value: m.normalizedValue, provenance: provRank });
    }
  }

  const sorted = [...buckets.values()].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return b.provenance - a.provenance;
  });

  if (sorted.length === 0) return null;
  const top = sorted[0]!;
  const tied = sorted.filter((s) => s.count === top.count);
  if (tied.length === 1 && top.count > 1) {
    return { canonicalValue: top.value, source: 'majority' };
  }
  if (tied.length === 1) {
    return { canonicalValue: top.value, source: 'provenance', provenance: 'highest-confidence mention' };
  }
  const byProv = [...tied].sort((a, b) => b.provenance - a.provenance)[0]!;
  return { canonicalValue: byProv.value, source: 'provenance', provenance: 'highest-confidence mention' };
}

function narrativeReportView(report: SiteRagReport): SiteRagReport {
  return { ...report, datasets: undefined };
}

/**
 * The FEMA zones the SED records ON the parcel: the exact polygon-intersection
 * `areaAnalysis.zoneAreas`, the back-compatible `zoneOverlap` fractions and the
 * scalar headline zone. Upper-cased; zones with zero area are excluded.
 */
export function siteFloodZoneSet(sed: SiteEvidenceDocument | null | undefined): ReadonlySet<string> {
  const flood = sed?.flood?.data;
  const zones = new Set<string>();
  const add = (zone: unknown) => {
    if (typeof zone === 'string' && zone.trim()) zones.add(zone.trim().toUpperCase());
  };
  for (const area of flood?.areaAnalysis?.zoneAreas ?? []) {
    if ((area.acres ?? 0) > 0 || (area.fraction ?? 0) > 0) add(area.zone);
  }
  for (const overlap of flood?.zoneOverlap ?? []) {
    if ((overlap.acres ?? 0) > 0 || (overlap.fraction ?? 0) > 0) add(overlap.zone);
  }
  add(flood?.floodZone);
  return zones;
}

export interface ReconcileInternalNumbersOptions {
  /**
   * Zones the SED records on the parcel (`siteFloodZoneSet`). When it holds two
   * or more zones, a report naming several of THEM is describing a multi-zone
   * parcel, not contradicting itself; only a zone outside the set conflicts.
   */
  siteFloodZones?: ReadonlySet<string>;
}

/** Find within-report numeric inconsistencies. */
export function reconcileInternalNumbers(
  report: SiteRagReport,
  opts: ReconcileInternalNumbersOptions = {},
): InternalNumberFinding[] {
  const text = reportFullText(narrativeReportView(report));
  const sectionTexts = extractSections(report);
  const mentions: NumberMention[] = [];

  for (const section of sectionTexts) {
    const prefixed = `# ${section.id}\n${section.content}`;
    const sectionMentions = extractNumberMentions(prefixed).map((m) => ({
      ...m,
      sectionId: m.sectionId ?? section.id,
    }));
    mentions.push(...sectionMentions);
  }
  if (mentions.length === 0) {
    mentions.push(...extractNumberMentions(text));
  }

  /*
   * SCOPE-AWARE SEVERITY (2026-08-05) — a self-contradiction requires two
   * values for the SAME quantity. Grouping on the canonical key alone treated
   * every distinct figure sharing a unit as a contradiction, which is how the
   * live 2026-08-04 Redhead report drew four BLOCKING findings for statements
   * that were all correct together:
   *   capacity  [550, 350, 50, 500] — campus total vs phase-1 vs available
   *   acreage   [1170.1, 65.7, 325.51] — site vs wetlands vs buildable
   *   voltage   [69, 230] — the two windings of a 230/69 kV substation
   *   distance  [204.9, 2.9] — nearest IX vs nearest substation
   *
   * For non-area quantities, prose does not scope reliably enough to SPLIT the mentions (an
   * excerpt is a whole sentence and routinely mentions several quantities),
   * so scope decides SEVERITY instead: when the mentions carry two or more
   * distinguishable quantity scopes the figures are plausibly different
   * things and the finding is ADVISORY; when they all share one scope the
   * report is contradicting itself about one quantity and it BLOCKS.
   */
  const byKey = new Map<string, NumberMention[]>();
  for (const m of mentions) {
    const group = `${m.canonicalKey}:${m.areaScope ?? ''}`;
    const list = byKey.get(group) ?? [];
    list.push(m);
    byKey.set(group, list);
  }

  const pairedVoltageInReport = /\d+\s*\/\s*\d+\s*kV/i.test(text);

  const findings: InternalNumberFinding[] = [];
  for (const keyMentions of byKey.values()) {
    const key = keyMentions[0]!.canonicalKey;
    if (keyMentions.length < 2) continue;
    const distinct = distinctNormalizedValues(key, keyMentions);
    if (distinct.length < 2) continue;
    /*
     * FEMA SET SEMANTICS (2026-09-30, live Holly Ridge environmental uv8zl6).
     * `flood_zone` was reconciled as a single scalar, so a parcel the exact
     * polygon analysis splits into Zone A (31.31 ac), AE (109.39 ac) and X
     * (276.39 ac) was reported as a self-contradiction ("A vs AE") every time
     * the report named both zones, and held the report. When the SED records
     * two or more zones on the parcel and EVERY zone the report names is one
     * of them, the mentions are the parcel's zone set, not a conflict. A zone
     * outside the SED set, or a single-zone SED, still blocks as before.
     */
    const siteZones = opts.siteFloodZones;
    if (key === 'flood_zone' && siteZones && siteZones.size >= 2
      && distinct.every((value) => siteZones.has(String(value).toUpperCase()))) continue;

    /*
     * Ambiguity requires TWO NAMED scopes. An unscoped mention ('default') is
     * not a distinguishing signal — it usually just lacks a keyword — so
     * counting it as one would downgrade genuine contradictions. Measured:
     * the audit-corpus dirty fixture asserts the SUBSTATION distance as both
     * 31 km and 16.09 km across separate sentences, three of its four mentions
     * scoping to 'substation'; counting 'default' turns that real
     * self-contradiction into a warning, which is a strictly worse trade than
     * the false positives it would clear.
     *
     * Residual false positives are acceptable HERE and nowhere else, because a
     * block is no longer a dead end: it raises an answerable truth question
     * (qaSeverityPolicy.buildTruthQuestions) whose answer persists as a SED
     * amendment. The cost is one operator interaction, not a dead report.
     *
     * Detection is unaffected — grouping stays one-per-canonical-key (see
     * above), so scope only ever chooses block-vs-warn.
     */
    const namedScopes = new Set(
      keyMentions
        .map((m) => m.quantityScope ?? mentionScope(key, m.scopeText ?? m.excerpt ?? ''))
        .filter((scope) => scope !== 'default'),
    );
    // A substation described anywhere in the report as dual-winding
    // ("230/69 kV") legitimately carries BOTH voltages — the live Redhead
    // report cites 230 kV for the interconnection and 69 kV for a substation
    // upgrade, which is not a contradiction.
    const pairedConstruction = key === 'voltage_kv' && pairedVoltageInReport;
    /*
     * Values that CO-OCCUR in one excerpt are playing different roles, not
     * contradicting. "Critical 250 MW Grid Power Deficit — the project
     * requires 300 MW but the utility can serve 50 MW" is precise writing;
     * reading 300 vs 250 as a self-contradiction inverts it (live 2026-08-05:
     * financial + supply-chain-risk blocked on exactly this). A real
     * contradiction states the quantity in SEPARATE places, so this cannot
     * mask one — the golden audit-corpus fixture (31 km vs 16.09 km in
     * different sentences) still blocks.
     */
    let statesTwoValues = keyMentions.some((m) => {
      const excerpt = m.scopeText ?? m.excerpt ?? '';
      if (!excerpt) return false;
      return distinct.filter((value) => excerptStatesValue(key, excerpt, value)).length >= 2;
    });
    // All conflicting values must be represented in a single-asset source
    // disclosure, not simply co-stated somewhere in a long excerpt.
    const unitAwareDisclosure = (key === 'interconnection_distance_km' || key === 'nuclear_proximity_km') && (() => {
      const groups = new Map<string, NumberMention[]>();
      for (const mention of keyMentions) {
        if (!mention.sentenceScope) continue;
        const id = JSON.stringify([mention.sectionId, mention.lineNumber, mention.sentenceScope]);
        const group = groups.get(id) ?? [];
        group.push(mention); groups.set(id, group);
      }
      for (const group of groups.values()) {
        const sentence = group[0]!.sentenceScope!;
        if (!/\b(?:conflict\w*|discrepan\w*|differ\w*|whereas|while|versus|vs\.?|compared|in\s+contrast|however|but|conversely|alternatively|resolve)\b/i.test(sentence)) continue;
        const scopes = new Set(group.map(mention => mention.quantityScope ?? mentionScope(key, mention.sentenceScope!)).filter(scope => scope !== 'default'));
        const assets = new Set(group.map(mention => mention.distanceAssetScope).filter(Boolean));
        // Refuse mixed explicit names even when nearest-token attribution is
        // ambiguous; a source-disclosure exemption cannot settle that ambiguity.
        const sentenceAssets = new Set([...sentence.matchAll(/\b([A-Z][\w'’-]*(?:\s+[A-Z][\w'’-]*){0,3})\s+(?:[Ss]ubstation|[Ss]witchyard|[Rr]eactor|[Nn]uclear\s+[Pp]lant)\b/g)]
          .map(match => match[1]!.toLowerCase()).filter(name => !/^(?:the|this|that|a|an|nearest|existing|proposed|nearby)$/.test(name)));
        if (scopes.size > 1 || assets.size > 1 || sentenceAssets.size > 1) continue;
        const indices = new Set(group.map(mention => distinct.findIndex(value => withinTolerance(key, value, mention.normalizedValue))));
        indices.delete(-1);
        if (indices.size >= 2 && indices.size === distinct.length) return true;
      }
      return false;
    })();
    const unitAwareOnly = unitAwareDisclosure && !statesTwoValues;
    statesTwoValues ||= unitAwareDisclosure;
    // A FEMA zone the SED does not record on the parcel is a real conflict
    // even when it shares a sentence with a recorded zone ("Zone AE and Zone
    // X" against an SED that maps only Zone X), so co-occurrence downgrades a
    // flood-zone finding to a warn only when no SED zone set is known (review
    // 2026-09-30).
    const zoneOutsideSite = key === 'flood_zone' && siteZones != null && siteZones.size > 0
      && distinct.some((value) => !siteZones.has(String(value).toUpperCase()));
    const coOccurring = statesTwoValues && !zoneOutsideSite;
    // Different voltage assets may coexist, but two values for the same
    // explicitly identified asset still contradict even with other assets present.
    const sameVoltageAssetConflict = key === 'voltage_kv' && ['substation', 'proposed-substation'].some(scope =>
      distinctNormalizedValues(key, keyMentions.filter(m => m.quantityScope === scope)).length > 1);
    const scopedArea = key === 'acreage' || isAreaKey(key);
    const ambiguous = !scopedArea && (pairedConstruction || ((namedScopes.size >= 2 || coOccurring) && !sameVoltageAssetConflict));
    // A flood-zone warn is two zones named together; rewriting one into the
    // other ("Zone AE" -> "A") would corrupt a correct multi-zone sentence.
    // A zone list that names a zone outside the SED set stays a block but is
    // never rewritten either ("Zone AE and Zone X" -> "X and Zone X"): the
    // extra zone needs a revision, not a substitution.
    const floodZoneAmbiguous = key === 'flood_zone' && (ambiguous || statesTwoValues);

    findings.push({
      canonicalKey: key,
      label: keyMentions[0]?.areaLabel ?? FACT_LABELS[key] ?? key,
      ...(isAreaKey(key) || unitAwareOnly ? { autocorrectBlocked: true } : {}),
      severity: ambiguous ? 'warn' : 'block',
      ...((key === 'voltage_kv' && ambiguous) || floodZoneAmbiguous ? { autocorrectBlocked: true } : {}),
      mentions: keyMentions,
      conflictingValues: distinct,
    });
  }
  return findings;
}

/**
 * Scope keywords that make two figures DIFFERENT QUANTITIES rather than a
 * contradiction. Order matters: the first match wins, so specific scopes
 * (phase-1) precede general ones (total).
 */
const SCOPE_PATTERNS: Partial<Record<ReconcilerCanonicalKey, ReadonlyArray<[string, RegExp]>>> = {
  capacity_mw: [
    /*
     * The phase1/total vocabularies carry the figure INLINE in real prose —
     * "the first 50 MW phase", "the full 300 MW build-out" — which the
     * original contiguous-phrase patterns ("first block", "full build") could
     * not match, so both scoped to 'default'. That was invisible while grid
     * headroom supplied a third scope by accident; once headroom was correctly
     * split out of this key (2026-08-14) the executive-summary body dropped to
     * ONE named scope and a legitimate phase-vs-total pair escalated from warn
     * to BLOCK. Recognising the real phrasing is the honest fix — the pinned
     * real-body count must be restored by better classification, never by
     * loosening the gate.
     */
    ['phase1', /\b(phase\s*(?:1|i|one)|initial|first\s+block|first\s+[\d,.]+\s*(?:MW|GW))\b/i],
    ['phase2', /\b(phase\s*(?:2|ii|two))\b/i],
    ['phase3', /\b(phase\s*(?:3|iii|three))\b/i],
    // A peer/comparable facility is a different subject from the target site.
    // Without this scope, a public-sentiment benchmark such as "Meta ...
    // (5,000 MW)" was majority-voted against the target campus capacity and
    // falsely held an otherwise coherent report.
    ['comparable', /\b(comparables?|benchmarks?|peer(?:\s+(?:site|project|facility))?|other\s+(?:site|project|facility))\b/i],
    ['available', /\b(available|headroom|indicated|deliverable|existing capacity)\b/i],
    ['deficit', /\b(deficit|shortfall|gap)\b/i],
    ['firm', /\b(firm|elcc|capacity credit)\b/i],
    ['nameplate', /\b(nameplate|gross)\b/i],
    ['required', /\b(required|requirement|demand|load target|need)\b/i],
    ['total', /\b(total|ultimate|campus|full build|at full|build-?out|full\s+[\d,.]+\s*(?:MW|GW))\b/i],
  ],
  acreage: [
    ['wetland', /\bwetland/i],
    // FEMA whole-parcel analysis uses one acreage as the calculation
    // denominator and another as the flood-affected subarea. They are two
    // different quantities even though both live in a flood paragraph.
    // Keep this specific scope ahead of the generic flood matcher so an
    // exact "parcel-polygon intersection (1,162.89 ac)" is not mistaken for
    // the 1%-annual-chance floodplain acreage it denominates.
    // The explicit intersection is the assessment denominator even when the
    // same serialized row later enumerates Zone A/AE/X subareas.
    ['flood-assessment-total', /\b(?:exact\s+)?parcel[- ]polygon\s+intersection\b/i],
    // Explicit total/denominator grammar binds THIS acreage to the assessment
    // whole before trailing zone names are considered. “Includes 325 acres of
    // floodplain” is deliberately absent: that is an affected subarea.
    ['flood-assessment-total', /\bflood\s+(?:analysis|assessment)\s+(?:parcel\s+)?area\s+(?:totals?|is|equals?|comprises?|measures?)\s+(?:(?:approximately|about|roughly|an?\s+estimated)\s+)?\d[\d,.]*\s*(?:acres?|ac\.?)(?=\s|\b|$)|\bflood\s+(?:analysis|assessment)\s+denominator\s*[:=]\s*(?:(?:approximately|about|roughly|an?\s+estimated)\s+)?\d[\d,.]*\s*(?:acres?|ac\.?)(?=\s|\b|$)/i],
    // An affected-area noun wins over nearby denominator language: “within
    // the flood assessment area, 325 acres lie in Zone A” describes the Zone
    // subarea, not the assessment total.
    ['floodplain', /\b(?:floodplain|floodway|sfha)\b|\bzone\s+(?:A(?:E|H|O)?|V(?:E)?|X)\b/i],
    ['flood-assessment-total', /\bassessment\s+(?:parcel\s+)?area\b|\bflood\s+(?:analysis|assessment)\s+(?:area|denominator)\b/i],
    ['floodplain', /\bflood\b/i],
    ['buildable', /\b(buildable|developable|usable|net)\b/i],
    ['generation', /\b(generation island|solar|pad|plant)\b/i],
    ['site', /\b(site|parcel|property|total|gross)\b/i],
  ],
  voltage_kv: [
    ['transmission', /\b(transmission|high[- ]side|primary)\b/i],
    ['distribution', /\b(distribution|low[- ]side|secondary)\b/i],
  ],
  interconnection_distance_km: [
    ['ix', /\b(internet exchange|\bix\b|peering|datacenter hub)\b/i],
    ['fiber', /\bfiber|fibre\b/i],
    ['pipeline', /\b(pipeline|gas lateral)\b/i],
    ['substation', /\b(substation|switchyard|poi|point of interconnection)\b/i],
  ],

  /*
   * FIN-1 (audit 3.8). A financial report states the SAME quantity on several
   * bases by design — an NPV at P10/P50/P90, an LCOE per phase, a
   * project-finance LCOE beside the pathway-benchmark one — and the report's
   * own Sensitivity and Monte-Carlo sections exist to show exactly that
   * spread. Without these scopes every such report self-contradicts on its
   * first page. Same rule as everywhere else in this table: scope decides
   * block-vs-warn, never whether the mentions are detected.
   */
  npv_usd: [
    ['p10', /\bP10\b|downside/i],
    ['p90', /\bP90\b|upside/i],
    ['p50', /\bP50\b|median/i],
    ['pathway', /\b(pathway|benchmark|value[- ]of[- ]served[- ]load)\b/i],
    ['scenario', /\bscenario\b/i],
  ],
  lcoe_usd_per_mwh: [
    ['pathway', /\b(pathway|benchmark)\b/i],
    ['phase1', /\bphase\s*(?:1|i|one)\b/i],
    ['phase2', /\bphase\s*(?:2|ii|two)\b/i],
    ['real', /\breal\b/i],
    ['nominal', /\bnominal\b/i],
  ],
  capex_total_usd: [
    ['deposit', /\bdeposit\b/i],
    ['phase1', /\bphase\s*(?:1|i|one)\b/i],
    ['phase2', /\bphase\s*(?:2|ii|two)\b/i],
    ['contingency', /\bcontingency\b/i],
  ],
  irr_pct: [
    ['equity', /\bequity\b/i],
    ['unlevered', /\bunlevered|pre[- ]tax\b/i],
  ],
  payback_years: [
    ['discounted', /\bdiscounted\b/i],
    ['simple', /\bsimple|undiscounted\b/i],
  ],
};

/**
 * Classify one mention into a quantity scope from its own excerpt. Returns
 * `'default'` when nothing distinguishes it — unscoped mentions still
 * contradict each other, so a genuine self-contradiction is preserved.
 */
export function mentionScope(key: ReconcilerCanonicalKey, excerpt: string): string {
  const patterns = SCOPE_PATTERNS[key];
  if (!patterns || !excerpt) return 'default';
  for (const [scope, re] of patterns) {
    if (re.test(excerpt)) return scope;
  }
  return 'default';
}

/** Rewrite non-canonical mentions when mode is autocorrect. Skips tier-1 cited lines. */
export function applyInternalNumberFixes(
  markdown: string,
  findings: InternalNumberFinding[],
  resolutions: Map<ReconcilerCanonicalKey, CanonicalResolution>,
  mode: InternalNumberFixMode = 'flagOnly',
): string {
  if (mode !== 'autocorrect' || findings.length === 0) return markdown;

  let out = markdown;
  const replacements: Array<{ from: string; to: string }> = [];

  for (const finding of findings) {
    if (finding.autocorrectBlocked) continue;
    const resolution = finding.resolution ?? resolutions.get(finding.canonicalKey);
    if (!resolution) continue;
    if (finding.canonicalKey === 'capacity_mw') {
      out = out.replace(/\b([\d,]+(?:\.\d+)?)\s*[-\s]?(MW|GW|megawatts?)\b/gi,
        (raw: string, number: string, units: string, offset: number) => {
          const lineStart = out.lastIndexOf('\n', offset) + 1;
          const nextLine = out.indexOf('\n', offset);
          const line = out.slice(lineStart, nextLine < 0 ? out.length : nextLine);
          const value = parseCapacityMw(number, units);
          const voted = finding.mentions.some((mention) => !mention.tier1Cited && mention.rawText === raw);
          if (!voted || value == null || withinTolerance('capacity_mw', value, resolution.canonicalValue)
            || NON_FACILITY_MW_CONTEXT_RE.test(line)
            || isNonFacilityCapacityMention(line, offset - lineStart, raw.length)
            || isComparisonBoundPrefix(line, offset - lineStart)
            || isTier1CitedLine(sentenceAround(line, offset - lineStart))) return raw;
          return formatCanonicalForDisplay('capacity_mw', resolution.canonicalValue, 'MW');
        });
      continue;
    }
    if (finding.canonicalKey === 'acreage') {
      out = out.replace(new RegExp(AREA_MENTION_RE.source, 'gi'), (raw, number, units, offset) => {
        const area = classifyAcreageMention(out, offset, raw.length);
        const value = parseAreaAcres(number, units);
        const voted = finding.mentions.some(mention => !mention.tier1Cited && mention.rawText === raw);
        if (area?.key !== 'acreage' || value == null || !voted || isTier1CitedLine(sentenceAround(out, offset)) || withinTolerance('acreage', value, resolution.canonicalValue)) return raw;
        return formatCanonicalForDisplay('acreage', resolution.canonicalValue, 'acres');
      });
      continue;
    }

    for (const mention of finding.mentions) {
      if (mention.tier1Cited) continue;
      if (withinTolerance(finding.canonicalKey, mention.normalizedValue, resolution.canonicalValue)) {
        continue;
      }
      const toValue = formatCanonicalForDisplay(finding.canonicalKey, resolution.canonicalValue, mention.units);
      replacements.push({ from: mention.rawText, to: toValue });
    }
  }

  replacements.sort((a, b) => b.from.length - a.from.length);
  for (const { from, to } of replacements) {
    if (!from || from === to) continue;
    out = out.split(from).join(to);
  }
  return out;
}

function formatCanonicalForDisplay(
  key: ReconcilerCanonicalKey,
  value: number | string,
  units?: string,
): string {
  if (typeof value === 'string') return value;
  switch (key) {
    case 'capacity_mw':
      return `${value} ${units ?? 'MW'}`;
    case 'interconnection_distance_km':
    case 'nuclear_proximity_km':
      return `${typeof value === 'number' ? value.toFixed(2).replace(/\.?0+$/, '') : value} km`;
    case 'voltage_kv':
      return `${value} kV`;
    case 'wetland_percent':
      return `${value}%`;
    case 'survey_area_acres':
    case 'wetland_area_acres':
    case 'flood_area_acres':
    case 'assessment_area_acres':
    case 'net_area_acres':
    case 'infrastructure_area_acres':
    case 'acreage':
      return `${value} acres`;
    /*
     * FIN-1 (audit 3.8). Autocorrect rewrites the mention's `rawText` with
     * this string, so a money value MUST come back at the scale it was
     * written: turning "$142M" into the literal "142000000" would be a
     * technically-correct edit that no reader would accept, and it would then
     * fail to re-parse as the same quantity on the next pass.
     */
    case 'npv_usd':
    case 'capex_total_usd':
      return formatUsdAtScale(value);
    case 'lcoe_usd_per_mwh':
      return `$${trimTrailingZeros(value.toFixed(2))}/MWh`;
    case 'capex_per_kw_usd':
      return `$${trimTrailingZeros(value.toFixed(2))}/kW`;
    case 'irr_pct':
    case 'hurdle_rate_pct':
    case 'wacc_pct':
    case 'capacity_factor_pct':
      return `${trimTrailingZeros(value.toFixed(1))}%`;
    case 'payback_years':
      return `${trimTrailingZeros(value.toFixed(1))} yr`;
    case 'annual_mwh':
      return `${Math.round(value).toLocaleString('en-US')} MWh`;
    default:
      return String(value);
  }
}

function trimTrailingZeros(fixed: string): string {
  return fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed;
}

/** Render a USD magnitude at the scale a reader would write it. */
function formatUsdAtScale(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1e9) return `$${trimTrailingZeros((value / 1e9).toFixed(2))}B`;
  if (abs >= 1e6) return `$${trimTrailingZeros((value / 1e6).toFixed(1))}M`;
  if (abs >= 1e3) return `$${Math.round(value).toLocaleString('en-US')}`;
  return `$${trimTrailingZeros(value.toFixed(2))}`;
}

function walkStringFields(
  obj: unknown,
  fn: (path: string, value: string) => string,
  path = '',
): unknown {
  if (typeof obj === 'string') return fn(path, obj);
  if (Array.isArray(obj)) {
    return obj.map((item, i) => walkStringFields(item, fn, `${path}[${i}]`));
  }
  if (obj && typeof obj === 'object') {
    if (path.includes('datasets')) return obj;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      out[k] = walkStringFields(v, fn, path ? `${path}.${k}` : k);
    }
    return out;
  }
  return obj;
}

function applyFixesToReport(
  report: SiteRagReport,
  findings: InternalNumberFinding[],
  resolutions: Map<ReconcilerCanonicalKey, CanonicalResolution>,
  mode: InternalNumberFixMode,
): SiteRagReport {
  if (mode !== 'autocorrect') return report;
  const fixed = walkStringFields(report, (path, text) => {
    if (path.includes('references') || path.includes('metadata') || path.includes('qaFindings')) {
      return text;
    }
    return applyInternalNumberFixes(text, findings, resolutions, 'autocorrect');
  }) as SiteRagReport;
  const md = (report as SiteRagReport & { markdown?: string }).markdown;
  if (typeof md === 'string') {
    (fixed as SiteRagReport & { markdown?: string }).markdown = applyInternalNumberFixes(
      md,
      findings,
      resolutions,
      'autocorrect',
    );
  }
  return fixed;
}

function enrichFindingsWithResolutions(
  findings: InternalNumberFinding[],
  report: SiteRagReport,
  evidence: EvidenceCache,
  auditOverrides?: import('../../types/site').SiteAuditOverride[],
): { findings: InternalNumberFinding[]; resolutions: Map<ReconcilerCanonicalKey, CanonicalResolution> } {
  const resolutions = new Map<ReconcilerCanonicalKey, CanonicalResolution>();
  const reconOverrides = new Map<string, string | number>();
  // Overrides are recorded by the audit UI under the PUBLIC CanonicalFactKey
  // ('capacityMW') while findings carry the INTERNAL key ('capacity_mw');
  // register both spellings so the lookup below matches either namespace.
  // An override with an empty key can settle nothing — skip it.
  const recordOverride = (key: string | undefined, value: string | number) => {
    if (!key) return;
    reconOverrides.set(key, value);
    const internal = toInternalCanonicalKey(key as CanonicalFactKey);
    if (internal) reconOverrides.set(internal, value);
  };
  for (const o of resolveActiveAuditOverrides(auditOverrides)) {
    const baseId = stripOverrideVersionSuffix(o.logicalKey ?? o.id);
    if ('type' in o && o.type === 'reconcile') {
      recordOverride(o.canonicalKey, o.canonicalValue);
    } else if ('fields' in o && o.fields && baseId.startsWith('recon:') && o.fields[0] === 'tier-2e' && o.fields[2] !== undefined && o.fields[3]) {
      recordOverride(o.fields[3], o.fields[2]);
    }
  }

  const enriched = findings.map((f) => {
    let resolution = resolveCanonicalValue(f.mentions, report, evidence);
    
    const userOverride = reconOverrides.get(f.canonicalKey);
    if (userOverride !== undefined) {
      // The UI records values as strings ("550"); numeric keys need a number
      // so the tolerance math applies instead of exact string comparison.
      // Non-numeric keys (grid_operator, flood_zone) keep the raw string.
      const numeric =
        typeof userOverride === 'string' ? Number(userOverride.replace(/,/g, '')) : userOverride;
      resolution = {
        canonicalValue: Number.isFinite(numeric) ? numeric : userOverride,
        source: 'majority', // Treat user override as majority to force application
      };
    }

    if (resolution) resolutions.set(f.canonicalKey, resolution);

    const tier1Mentions = f.mentions.filter((m) => m.tier1Cited);
    let autocorrectBlocked = f.autocorrectBlocked === true;
    if (resolution && tier1Mentions.length > 0) {
      const tier1Disagrees = tier1Mentions.some(
        (m) => !withinTolerance(f.canonicalKey, m.normalizedValue, resolution!.canonicalValue),
      );
      if (tier1Disagrees && userOverride === undefined) autocorrectBlocked = true; // User override unblocks autocorrect
    }

    return { ...f, resolution: resolution ?? undefined, autocorrectBlocked };
  });
  return { findings: enriched, resolutions };
}

const RECONCILIATION_FOOTNOTE = '(values reconciled internally — see Audit tab)';

/** Run reconciler + optional autocorrect; attaches qaFindings.internalNumberFindings. */
export function applyInternalNumberGate(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  opts: { autocorrect?: boolean; auditOverrides?: import('../../types/site').SiteAuditOverride[] } = {},
): SiteRagReport {
  const evidence = buildEvidenceCacheFromSed(sed);
  const reconcileOptions: ReconcileInternalNumbersOptions = { siteFloodZones: siteFloodZoneSet(sed) };
  const rawFindings = reconcileInternalNumbers(report, reconcileOptions);
  const { findings, resolutions } = enrichFindingsWithResolutions(rawFindings, report, evidence, opts.auditOverrides);

  const mode: InternalNumberFixMode = opts.autocorrect ? 'autocorrect' : 'flagOnly';
  let out = applyFixesToReport(report, findings, resolutions, mode);

  let fixCount = 0;
  if (mode === 'autocorrect') {
    for (const f of findings) {
      if (f.autocorrectBlocked) continue;
      const resolution = resolutions.get(f.canonicalKey);
      if (!resolution) continue;
      for (const m of f.mentions) {
        if (m.tier1Cited) continue;
        if (!withinTolerance(f.canonicalKey, m.normalizedValue, resolution.canonicalValue)) {
          fixCount += 1;
        }
      }
    }
  }

  if (fixCount > 0) {
    const footnote = `\n\n_${fixCount} ${RECONCILIATION_FOOTNOTE}_`;
    const md = (out as SiteRagReport & { markdown?: string }).markdown;
    if (typeof md === 'string' && !md.includes(RECONCILIATION_FOOTNOTE)) {
      (out as SiteRagReport & { markdown?: string }).markdown = md + footnote;
    }
  }

  // Repair attempts are not evidence that every conflict was resolved. Check
  // the text actually produced by the existing fixer, including any protected
  // or unrewritable mentions, before stamping the current blocking findings.
  // Keep the original findings below as diagnostic provenance only.
  const currentFindings = mode === 'autocorrect'
    ? enrichFindingsWithResolutions(reconcileInternalNumbers(out, reconcileOptions), out, evidence, opts.auditOverrides).findings
    : findings;

  if (currentFindings.length === 0 && fixCount === 0) {
    // ZOMBIE-STAMP CLEAR (2026-08-14) — third instance of the same class
    // (R-78 providerPrecedence, R-75 caveatHonesty, R-67 here): an
    // update-mode draft INHERITS its parent's qaFindings, so returning the
    // untouched report on a clean pass kept a stale internalNumberFindings
    // stamp alive through every regeneration. A clean pass must CLEAR it.
    const inherited = report.qaFindings?.internalNumberFindings;
    if (inherited?.length) {
      return {
        ...report,
        qaFindings: { ...report.qaFindings, internalNumberFindings: [] },
      };
    }
    return report;
  }

  out = {
    ...out,
    qaFindings: {
      ...(out.qaFindings ?? {}),
      internalNumberFindings: currentFindings.map((f) => ({
        canonicalKey: f.canonicalKey,
        label: f.label,
        severity: f.severity,
        mentions: f.mentions.map((m) => ({
          value: m.value,
          normalizedValue: m.normalizedValue,
          units: m.units,
          sectionId: m.sectionId,
          lineNumber: m.lineNumber,
          excerpt: m.excerpt,
          tier1Cited: m.tier1Cited,
          rawText: m.rawText,
        })),
        conflictingValues: f.conflictingValues,
        resolution: f.resolution,
        autocorrectBlocked: f.autocorrectBlocked,
      })),
    },
  };

  if (findings.length > 0) {
    console.info('[R-67 internal numbers] Inconsistencies:', {
      reportId: out.id,
      facts: findings.map((f) => ({
        key: f.canonicalKey,
        values: f.conflictingValues,
        blocked: f.autocorrectBlocked,
      })),
      fixCount,
      remainingFindingCount: currentFindings.length,
    });
  }

  return out;
}

export { toInternalCanonicalKey };
